import { useRef, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';

import { PrintableInstruction } from 'src/Components/PrintTemplates/PrintableInstruction';
import { InstructionDto } from 'src/Services/API/generated';

interface PrintInstructionOptions {
  instruction: InstructionDto;
  assetName: string;
  exportUrl: string;
}

const usePrintInstruction = () => {
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  const printInstruction = useCallback(
    ({ instruction, assetName, exportUrl }: PrintInstructionOptions) => {
      try {
        if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
          document.body.removeChild(printFrameRef.current);
          printFrameRef.current = null;
        }

        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        printFrameRef.current = iframe;

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
          throw new Error('Impossible to gain access to print window');
        }

        const content = ReactDOMServer.renderToString(
          <PrintableInstruction
            instruction={instruction}
            assetName={assetName}
            exportUrl={exportUrl}
          />
        );

        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${instruction.title}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; padding: 0; }
              @page { size: A4; margin: 0; }
              @media print {
                body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
              .slate-paragraph { margin: 4px 0; }
              .slate-heading { font-size: 16px; font-weight: bold; margin: 8px 0 4px; }
              .slate-list { padding-left: 20px; margin: 4px 0; }
              .slate-list-item { margin: 2px 0; }
              .slate-block { margin: 4px 0; }
            </style>
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                if (document.fonts && document.fonts.ready) {
                  document.fonts.ready.then(function() {
                    window.print();
                  }).catch(function() {
                    window.print();
                  });
                } else {
                  setTimeout(function() { window.print(); }, 1000);
                }
              });
            </script>
          </head>
          <body>${content}</body>
          </html>
        `;

        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();

        const cleanup = () => {
          setTimeout(() => {
            if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
              document.body.removeChild(printFrameRef.current);
              printFrameRef.current = null;
            }
          }, 2000);
        };

        printWindow.addEventListener('afterprint', cleanup);
        setTimeout(cleanup, 10000);
      } catch (error) {
        console.error('Error while printing instruction:', error);
      }
    },
    []
  );

  return { printInstruction };
};

export default usePrintInstruction;
